/**
 * Text Editor Sheet - native bottom sheet for editing long text content.
 * iOS-style bottom sheet with proper Telegram integration.
 */

import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { getTelegram, haptic } from "../../../../lib/telegram.ts";

interface FullscreenTextEditorProps {
  isOpen: boolean;
  title: string;
  value: string;
  placeholder?: string;
  onSave: (value: string) => void;
  onClose: () => void;
}

export function FullscreenTextEditor({
  isOpen,
  title,
  value,
  placeholder,
  onSave,
  onClose,
}: FullscreenTextEditorProps) {
  const [localValue, setLocalValue] = useState(value);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollPositionRef = useRef(0);

  // Handle open/close with animation
  useEffect(() => {
    if (isOpen) {
      // Сохраняем позицию скролла
      scrollPositionRef.current = window.scrollY;

      setLocalValue(value);
      setIsVisible(true);

      // Small delay to ensure DOM is ready for animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      // Closing: animate out, then unmount
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, value]);

  // Блокировка скролла через touch events (для мобильных)
  useEffect(() => {
    if (!isVisible) return;

    // Блокируем скролл через overflow на html и body
    const html = document.documentElement;
    const body = document.body;

    const originalHtmlOverflow = html.style.overflow;
    const originalBodyOverflow = body.style.overflow;
    const originalHtmlHeight = html.style.height;
    const originalBodyHeight = body.style.height;

    html.style.overflow = "hidden";
    html.style.height = "100%";
    body.style.overflow = "hidden";
    body.style.height = "100%";

    // Блокируем touch scroll на backdrop и sheet
    const preventScroll = (e: TouchEvent) => {
      // Разрешаем скролл внутри textarea контейнера
      const target = e.target as HTMLElement;
      if (target.closest(".sheet-content")) {
        return;
      }
      e.preventDefault();
    };

    document.addEventListener("touchmove", preventScroll, { passive: false });

    return () => {
      html.style.overflow = originalHtmlOverflow;
      html.style.height = originalHtmlHeight;
      body.style.overflow = originalBodyOverflow;
      body.style.height = originalBodyHeight;

      document.removeEventListener("touchmove", preventScroll);

      // Восстанавливаем позицию скролла
      window.scrollTo(0, scrollPositionRef.current);
    };
  }, [isVisible]);

  // Auto-focus textarea when animation completes (with preventScroll)
  useEffect(() => {
    if (isAnimating && textareaRef.current) {
      const timer = setTimeout(() => {
        textareaRef.current?.focus({ preventScroll: true });
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isAnimating]);

  // Handle Telegram BackButton - close sheet without saving
  useEffect(() => {
    if (!isVisible) return;

    const tg = getTelegram();
    if (!tg) return;

    const handleBack = () => {
      haptic("light");
      handleClose();
    };

    tg.BackButton.onClick(handleBack);

    return () => {
      tg.BackButton.offClick(handleBack);
    };
  }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = () => {
    haptic("medium");
    // Only call onSave if there are actual changes
    if (localValue !== value) {
      onSave(localValue);
    }
    onClose();
  };

  const handleClose = () => {
    haptic("light");
    setLocalValue(value); // Reset to original
    onClose();
  };

  const hasChanges = localValue !== value;

  if (!isVisible) return null;

  // Используем Portal для рендеринга вне Layout (который имеет overflow-hidden)
  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[9998] bg-black transition-opacity duration-300 ${
          isAnimating ? "opacity-40" : "opacity-0"
        }`}
        onClick={handleClose}
      />

      {/* Sheet */}
      <div
        className={`fixed inset-x-0 bottom-0 z-[9999] bg-white rounded-t-[20px] flex flex-col shadow-[0_-4px_24px_rgba(0,0,0,0.12)] transition-transform duration-300 ease-out ${
          isAnimating ? "translate-y-0" : "translate-y-full"
        }`}
        style={{
          maxHeight: "90vh",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-slate-300" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-3 border-b border-slate-100 flex-shrink-0">
          <h2 className="text-[17px] font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={handleSave}
            className={`text-[15px] font-semibold px-4 py-1.5 rounded-full transition-all active:scale-95 ${
              hasChanges
                ? "text-white bg-[var(--color-accent)]"
                : "text-slate-400 bg-slate-100"
            }`}
          >
            Готово
          </button>
        </div>

        {/* Textarea container */}
        <div className="sheet-content flex-1 overflow-auto min-h-[200px]">
          <textarea
            ref={textareaRef}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            placeholder={placeholder}
            className="w-full h-full min-h-[250px] px-4 py-4 text-[16px] leading-relaxed text-slate-900 placeholder:text-slate-400 bg-transparent resize-none outline-none"
          />
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-slate-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <span className="text-[12px] text-slate-400">
              {localValue.length} символов
            </span>
            {hasChanges && (
              <span className="text-[12px] text-amber-500 font-medium">
                Изменено
              </span>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
